import React, { ChangeEvent, Component, ReactNode } from 'react';
import { RootState } from '../../../../redux/store';
import { ChartState } from '../../../../redux/reducers/ChartReducer';
import { connect, ConnectedProps } from 'react-redux';
import { changeChart, changeChartEditorOptions, changeChartResponse } from '../../../../redux/actions/ChartActions';
import CustomLoading from '../../../custom/CustomLoading';
import { MetricItem } from '../../../../domain/chart/ChartDomains';
import { ChartType, Syntax } from '@trlogic/ui-pack';
import DataType from '../../../../constant/DataType';
import MessageUtil from '../../../../util/MessageUtil';
import { MetricType } from '../../../../domain/chart/editor/Metric';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../../custom/FormicaSearchSelectBox';
import ChartValueFactory from '../../../../util/ChartValueFactory';
import FormicaInput from 'components/custom/FormicaInput';
import { User } from '../../../../domain/rbac/RbacDomains';
import { TableVariableType } from '../../../../../../server/src/lib/domain/chart/ChartDomain';
import FormicaColorPicker from '../../../custom/color-palette/FormicaColorPicker';
import { generateColorPalette } from '../../../custom/color-palette/ColorPaletteFactory';
import MetricFilterItem from './MetricFilterItem';
import LanguageUtil from '../../../../util/LanguageUtil';

class MetricContainer extends Component<Props, State> {
  public state = new State();

  render() {
    const {
      chart: { chartEditorOptions },
    } = this.props;
    const { activeMetricItem } = chartEditorOptions;
    if (activeMetricItem == undefined) return <CustomLoading />;
    return (
      <div className={'flex flex-col gap-y-4 w-full max-h-[720px] px-4 py-4 overflow-y-scroll'} onContextMenu={(e) => e.preventDefault()}>
        <div className={'flex flex-col w-full bg-white rounded-md px-4 py-4 border-[0.5px] border-f-black-5 gap-y-3'}>
          <label className={'text-primary text-medium font-semibold text-[13px]'}>
            {LanguageUtil.get({ id: 'common.label.data', defaultMessage: 'Data' })}
          </label>
          <div>
            <label htmlFor={'functions-dropdown'} className={'text-f-black-2 text-sm mt-2'}>
              {LanguageUtil.get({ id: 'common.label.functions', defaultMessage: 'Functions' })}
            </label>
            <FormicaSearchSelectBox
              id={'functions-dropdown'}
              rounded={false}
              customSize={'medium'}
              options={this.getFunctionOptionsByChartType()}
              onChange={(e) => this.onChangeFunctionValue(e)}
              value={activeMetricItem.metric.type}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.function', defaultMessage: 'Function' })}
            />
          </div>
          <div>
            <label htmlFor={'advanced-dropdown'} className={'text-f-black-2 w-full text-sm mt-2'}>
              Field
            </label>
            <FormicaSearchSelectBox
              id={'advanced-dropdown'}
              options={this.getAvailableElasticVariables()}
              icon={this.getAvailableElasticVariables().find((item) => item.value == activeMetricItem.metric.field.name)?.icon || 'text'}
              value={activeMetricItem.metric.field.name || 'Select a field'}
              customSize={'medium'}
              rounded={false}
              onChange={(e: FormicaSelectBoxItem) => this.onChangeField(e)}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.selectField', defaultMessage: 'Select a field' })}
            />
          </div>
          {
            <div className={'flex flex-col'}>
              <div className={'flex w-full'}>
                <MetricFilterItem
                  isElasticExpression={true}
                  header={LanguageUtil.get({ id: 'common.label.filter', defaultMessage: 'Filter' })}
                  key={activeMetricItem.name + 'filter'}
                  syntax={activeMetricItem.metric.filter}
                  onChangeSyntax={this.onChangeFilter}
                />
              </div>
              {/*<div className={"flex w-full"}>
                <MetricFilterItem isElasticExpression={false} header={"Transformer"} key={activeMetricItem.name + "transformer"}
                                  syntax={activeMetricItem.metric.transformer}
                                  onChangeSyntax={this.onChangeTransformer}
                />
              </div>*/}
            </div>
          }
        </div>
        {this.getAppearance()}
      </div>
    );
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    if (prevProps.chart.chartEditorOptions.transactionVariables != this.props.chart.chartEditorOptions.transactionVariables) {
      this.setState({ fields: this.getFieldsByFunction() });
    }
  }

  private onChangeFunctionValue = (e: FormicaSelectBoxItem) => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    const horizontalAxisItem = this.getActiveAxisItem();
    horizontalAxisItem.metric.type = e.value as MetricType;
    horizontalAxisItem.name = e.value + ' of ' + horizontalAxisItem.metric.field.name;
    horizontalAxisItem.metric.appearance.name = e.value + ' of ' + horizontalAxisItem.metric.field.name;
    horizontalAxisItem.metric.field = {} as TableVariableType;
    this.checkMetricsNameIsSame();
    changeChartEditorOptions(chartEditorOptions);
    this.setState({ fields: this.getFieldsByFunction() });
  };

  private getFieldsByFunction = (): FormicaSelectBoxItem[] => {
    return this.getAvailableElasticVariables();
  };

  private onChangeField = (e: FormicaSelectBoxItem) => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    const horizontalAxisItem = this.getActiveAxisItem();
    horizontalAxisItem.metric.field.name = e.value;
    horizontalAxisItem.metric.field.dataType = e.dataType as DataType;
    horizontalAxisItem.name = horizontalAxisItem.metric.type + ' of ' + e.label;
    horizontalAxisItem.metric.appearance.name = horizontalAxisItem.metric.type + ' of ' + e.label;
    this.checkMetricsNameIsSame();
    changeChartEditorOptions(chartEditorOptions);
  };

  private checkMetricsNameIsSame = () => {
    const {
      chart: { chartEditorOptions },
    } = this.props;
    const horizontalAxisItem = this.getActiveAxisItem();
    const filteredAxis = chartEditorOptions.metricItem.filter((item: MetricItem) => item.metric.id != horizontalAxisItem.metric.id);
    filteredAxis.map((item: MetricItem) => {
      if (item.name == horizontalAxisItem.name) {
        item.name = horizontalAxisItem.name + `-${chartEditorOptions.metricItem.length - 1}`;
        item.metric.appearance.name = horizontalAxisItem.name + `-${chartEditorOptions.metricItem.length - 1}`;
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.sameFunction', defaultMessage: 'Same function and field detected on metrics.' }),
          detail: LanguageUtil.get(
            {
              id: 'messages.warning.checkMetrics',
              defaultMessage: `${item.name} changed automatically. Please check metrics name.`,
            },
            { 0: `${item.name}` },
          ),
        });
      }

      return item;
    });
  };

  private onChangeFilter = (syntax: Syntax) => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    const horizontalAxisItem = this.getActiveAxisItem();
    horizontalAxisItem.metric.filter = syntax;
    changeChartEditorOptions(chartEditorOptions);
  };

  private onChangeTransformer = (syntax: Syntax) => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    const horizontalAxisItem = this.getActiveAxisItem();
    horizontalAxisItem.metric.transformer = syntax;
    changeChartEditorOptions(chartEditorOptions);
  };

  private onChangeAppearanceName = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    const horizontalAxisItem = this.getActiveAxisItem();
    if (chartEditorOptions.segmentItem?.segments.groupBy && chartEditorOptions.segmentItem?.segments.groupBy.orderBy.field.name == horizontalAxisItem.name) {
      chartEditorOptions.segmentItem.segments.groupBy.orderBy.field.name = e.currentTarget.value;
    }
    horizontalAxisItem.metric.appearance.name = e.currentTarget.value;
    horizontalAxisItem.name = e.currentTarget.value;
    changeChartEditorOptions(chartEditorOptions);
  };

  private onChangeAppearanceSuffix = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    const horizontalAxisItem = this.getActiveAxisItem();
    horizontalAxisItem.metric.appearance.suffix = e.currentTarget.value;
    changeChartEditorOptions(chartEditorOptions);
  };

  private onChangeAppearancePrefix = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    const horizontalAxisItem = this.getActiveAxisItem();
    horizontalAxisItem.metric.appearance.prefix = e.currentTarget.value;
    changeChartEditorOptions(chartEditorOptions);
  };

  private onChangeAppearanceDecimal = (value: number) => {
    const {
      chart: { chartEditorOptions },
      changeChartEditorOptions,
    } = this.props;
    const horizontalAxisItem = this.getActiveAxisItem();
    horizontalAxisItem.metric.appearance.decimal = value;
    changeChartEditorOptions(chartEditorOptions);
  };

  private getActiveAxisItem = (): MetricItem => {
    const {
      chart: { chartEditorOptions },
    } = this.props;
    const { activeMetricItem } = chartEditorOptions;
    return activeMetricItem ? activeMetricItem : ({ metric: ChartValueFactory.initializeMetric(chartEditorOptions?.metricItem?.length) } as MetricItem);
  };

  private getAvailableElasticVariables = (): FormicaSelectBoxItem[] => {
    const {
      chart: { chartEditorOptions },
      user,
    } = this.props;
    const variables: FormicaSelectBoxItem[] = [];
    if (chartEditorOptions?.transactionVariables && Array.isArray(chartEditorOptions?.transactionVariables)) {
      const metricType = chartEditorOptions.activeMetricItem?.metric.type;
      if (metricType === 'Count' || metricType === 'Cumulative Count') {
        variables.push({
          label: 'Record',
          value: 'Record',
          icon: 'number',
          dataType: DataType.STRING,
        });
      }
      const allVariables = [
        ...ChartValueFactory.initializeElasticVariablesForChartMetricAndSegment(user?.activeRiskType as string),
        ...chartEditorOptions?.transactionVariables,
      ];
      const filteredTableVariables = allVariables
        .filter((field) => {
          if (metricType === 'Count') {
            return true;
          }
          return field.dataType === DataType.NUMBER;
        })
        .map((filteredFields: FormicaSelectBoxItem) => ({
          label: filteredFields.label,
          value: filteredFields.value,
          icon: this.getIconNameByDataType(filteredFields.dataType as DataType),
          dataType: filteredFields.dataType,
        }));
      return [...variables, ...filteredTableVariables];
    }
    return [] as FormicaSelectBoxItem[];
  };

  private getIconNameByDataType(dataType: DataType) {
    switch (dataType) {
      case DataType.NUMBER:
        return 'number';
      case DataType.STRING:
        return 'text';
      case DataType.DATE:
        return 'calendar';
    }
  }

  private getAppearance = (): ReactNode => {
    const {
      chart: { chart, chartEditorOptions },
    } = this.props;
    const { activeMetricItem } = chartEditorOptions;
    if (activeMetricItem == undefined) return <CustomLoading />;
    if (chart.type == ChartType.PIE || chart.type == ChartType.DONUT) {
      return (
        <div className={`flex flex-col w-full bg-[#F7F8FA] items-start gap-y-4  py-4 px-4 rounded border-[0.5px] border-[#E0E4E8] `}>
          <p className={'text-lg text-primary'}>{LanguageUtil.get({ id: 'common.label.appearance', defaultMessage: 'Appearance' })}</p>
          <div className={'flex flex-col w-full'}>
            <label className={'text-f-black-2 text-sm'} htmlFor={'name-dropdown'}>
              {LanguageUtil.get({ id: 'common.label.name', defaultMessage: 'Name' })}
            </label>
            <FormicaInput
              id={'name-dropdown'}
              value={activeMetricItem.metric.appearance.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => this.onChangeAppearanceName(e)}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.name', defaultMessage: 'Name' })}
            />
          </div>
          {/*<div className={"flex flex-col w-full"}>
              <label className={"text-f-black-2 text-sm"} htmlFor={"suffix-input"}>Suffix</label>
              <FormicaInput id={"suffix-input"}
                            value={activeMetricItem.metric.appearance.suffix}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => this.onChangeAppearanceSuffix(e)}
                            placeholder={"Suffix"}/>
            </div>*/}
        </div>
      );
    }
    return (
      <div className={`flex flex-col w-full h-full bg-[#F7F8FA] items-start gap-y-4  py-4 px-4 rounded border-[0.5px] border-[#E0E4E8] `}>
        <p className={'text-lg font-semibold text-primary'}>{LanguageUtil.get({ id: 'common.label.appearance', defaultMessage: 'Appearance' })}</p>
        <div className={'flex flex-col w-full'}>
          <label className={'text-f-black-2 text-sm'} htmlFor={'name-dropdown'}>
            {LanguageUtil.get({ id: 'common.label.name', defaultMessage: 'Name' })}
          </label>
          <FormicaInput
            id={'name-dropdown'}
            value={activeMetricItem.metric.appearance.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) => this.onChangeAppearanceName(e)}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.name', defaultMessage: 'Name' })}
          />
        </div>
        {
          <div className={'flex flex-col w-full'}>
            <label className={'text-f-black-2 text-sm'} htmlFor={'prefix-input'}>
              Prefix
            </label>
            <FormicaInput
              id={'prefix-input'}
              value={activeMetricItem.metric.appearance.prefix}
              onChange={(e: ChangeEvent<HTMLInputElement>) => this.onChangeAppearancePrefix(e)}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.prefix', defaultMessage: 'Prefix' })}
            />
          </div>
        }
        {
          <div className={'flex flex-col w-full'}>
            <label className={'text-f-black-2 text-sm'} htmlFor={'suffix-input'}>
              {LanguageUtil.get({ id: 'common.label.suffix', defaultMessage: 'Suffix' })}
            </label>
            <FormicaInput
              id={'suffix-input'}
              value={activeMetricItem.metric.appearance.suffix}
              onChange={(e: ChangeEvent<HTMLInputElement>) => this.onChangeAppearanceSuffix(e)}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.suffix', defaultMessage: 'Suffix' })}
            />
          </div>
        }
        {/*<div className={"flex items-center justify-between w-full"}>
            <label className={"text-f-black-2 text-sm"}>Decimals</label>
            <FormicaCounter value={activeMetricItem.metric.appearance.decimal as number}
                            onChange={this.onChangeAppearanceDecimal}/>
          </div>*/}
        <div className={'flex flex-col w-full items-center'}>
          <FormicaColorPicker value={activeMetricItem.metric.appearance.colorSettings.activeColor} onChange={this.updateChartDataColors} />
        </div>
      </div>
    );
  };
  private onChangeColorSettings = (activeColor: string) => {
    const {
      changeChartEditorOptions,
      chart: { chartEditorOptions },
    } = this.props;
    const activeAxisItem = this.getActiveAxisItem();
    activeAxisItem.metric.appearance.colorSettings.colorPalette = generateColorPalette(activeColor, 10);
    activeAxisItem.metric.appearance.colorSettings.activeColor = activeColor;
    changeChartEditorOptions(chartEditorOptions);
    this.updateChartDataColors(activeColor);
  };

  private updateChartDataColors = (activeColor: string) => {
    const {
      chart: { chart, chartEditorOptions, chartResponse },
      changeChartEditorOptions,
      changeChartResponse,
    } = this.props;
    if (chart.type == ChartType.INDICATOR) return;
    const metricItem = this.getActiveAxisItem();
    metricItem.metric.appearance.colorSettings.activeColor = activeColor as string;
    changeChartEditorOptions(chartEditorOptions);
    if (chartResponse) {
      chartResponse.datasets = chartResponse.datasets.map((dataset) => {
        if (dataset.label == metricItem.metric.appearance.name) {
          if (chart.type == ChartType.DONUT || chart.type == ChartType.PIE) {
            dataset.backgroundColor = generateColorPalette(activeColor, 10);
            dataset.borderColor = activeColor;
          } else {
            dataset.backgroundColor = activeColor as string;
            dataset.borderColor = activeColor as string;
          }
        }
        return dataset;
      });
      changeChartResponse(chartResponse);
    }
  };

  private getFunctionOptionsByChartType() {
    const {
      chart: { chart },
    } = this.props;
    return [
      {
        label: LanguageUtil.get({ id: 'common.aggregations.count', defaultMessage: 'Count' }),
        value: 'Count',
      },
      {
        label: LanguageUtil.get({ id: 'common.aggregations.sum', defaultMessage: 'Sum' }),
        value: 'Sum',
      },
      {
        label: LanguageUtil.get({ id: 'common.aggregations.average', defaultMessage: 'Average' }),
        value: 'Average',
      },
      {
        label: LanguageUtil.get({ id: 'common.aggregations.min', defaultMessage: 'Minimum' }),
        value: 'Minimum',
      },
      {
        label: LanguageUtil.get({ id: 'common.aggregations.max', defaultMessage: 'Maximum' }),
        value: 'Maximum',
      },
    ];
  }
}

class State {}

interface Props extends ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  chart: state.chart as ChartState,
  user: state.auth.user as User,
});
const mapDispatchToProps = { changeChart, changeChartEditorOptions, changeChartResponse };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(MetricContainer);
